import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Box,
  Alert,
  Divider,
  Link,
  List,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const DataDeletion = () => {
  const [email, setEmail] = useState('');
  const [platform, setPlatform] = useState('');
  const [details, setDetails] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(`Data Deletion Request - ${platform}`);
    const body = encodeURIComponent(
      `DATA DELETION REQUEST\n` +
      `=====================\n\n` +
      `Email: ${email}\n` +
      `Platform: ${platform}\n` +
      `Details: ${details || 'None provided'}\n\n` +
      `Please delete all data associated with this email address.\n\n` +
      `Submitted via: ${window.location.href}`
    );
    window.location.href = `mailto:mikel.smart@smartindustriesllc.com?subject=${subject}&body=${body}`;
    setSubmitted(true);
  };

  const dataRows = [
    {
      type: 'Breach check queries (email/phone)',
      stored: 'No',
      storedColor: 'success.main',
      location: 'Not stored - processed in real-time',
    },
    {
      type: 'Identity recovery progress (app)',
      stored: 'Yes (encrypted)',
      storedColor: 'warning.main',
      location: 'Your device only',
    },
    {
      type: 'Monitored identifiers (app)',
      stored: 'Yes (encrypted)',
      storedColor: 'warning.main',
      location: 'Your device only',
    },
    {
      type: 'Crash reports (app)',
      stored: 'Yes (anonymized)',
      storedColor: 'warning.main',
      location: 'Sentry (third-party)',
    },
  ];

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      {/* Header */}
      <Typography
        variant="h3"
        sx={{ textAlign: 'center', color: 'primary.main', fontWeight: 700, mb: 4 }}
      >
        Request Data Deletion
      </Typography>

      {/* Intro */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
          We respect your privacy and your right to control your personal data. Use this page to request
          deletion of any data we may have associated with you.
        </Typography>
      </Paper>

      {/* Website Users */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" sx={{ color: 'primary.main', mb: 2, fontWeight: 600 }}>
          For Website Users
        </Typography>
        <Typography variant="body1" paragraph>Our website is designed with privacy in mind:</Typography>
        <List dense disablePadding sx={{ pl: 2 }}>
          <ListItem disableGutters sx={{ py: 0.25 }}>
            <ListItemText primary={<><strong>Breach check queries</strong> are <strong>NOT stored</strong> on our servers - they are processed in real-time and immediately discarded</>} />
          </ListItem>
          <ListItem disableGutters sx={{ py: 0.25 }}>
            <ListItemText primary={<><strong>No user accounts</strong> are created on the website</>} />
          </ListItem>
          <ListItem disableGutters sx={{ py: 0.25 }}>
            <ListItemText primary={<><strong>No personal information</strong> is permanently stored</>} />
          </ListItem>
        </List>
        <Typography variant="body1" sx={{ mt: 1.5 }}>
          If you've used the Identity Recovery feature on our website, any progress data is stored temporarily
          and can be cleared by closing your browser or clearing cookies.
        </Typography>
      </Paper>

      {/* Mobile App Users */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" sx={{ color: 'primary.main', mb: 2, fontWeight: 600 }}>
          For Mobile App Users (Smart Industries)
        </Typography>
        <Typography variant="body1" paragraph>
          The Smart Industries Android app stores all data <strong>locally on your device</strong> using
          encrypted storage. No data is sent to or stored on our servers.
        </Typography>
        <Typography variant="h6" sx={{ color: 'primary.main', mt: 2, mb: 1 }}>
          To delete your data from the app:
        </Typography>
        <List dense sx={{ pl: 2 }}>
          {[
            <>Open the <strong>Smart Industries</strong> app</>,
            <>Navigate to <strong>Settings</strong> (gear icon)</>,
            <>Tap <strong>"Delete All Data"</strong></>,
            'Confirm the deletion when prompted',
          ].map((item, i) => (
            <ListItem key={i} disableGutters sx={{ py: 0.25 }}>
              <ListItemText primary={<>{i + 1}. {item}</>} />
            </ListItem>
          ))}
        </List>
        <Typography variant="body1" sx={{ mt: 1.5 }}>
          <strong>Alternative:</strong> Uninstalling the app will automatically remove all stored data from your device.
        </Typography>
      </Paper>

      {/* Data Collection Table */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" sx={{ color: 'primary.main', mb: 2, fontWeight: 600 }}>
          What Data Do We Collect?
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'primary.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Data Type</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Stored?</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Location</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dataRows.map((row, i) => (
                <TableRow key={i} sx={{ backgroundColor: i % 2 === 1 ? 'grey.50' : 'inherit' }}>
                  <TableCell>{row.type}</TableCell>
                  <TableCell sx={{ color: row.storedColor, fontWeight: 600 }}>{row.stored}</TableCell>
                  <TableCell>{row.location}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Deletion Request Form */}
      <Paper
        sx={{
          p: 4,
          mb: 3,
          background: 'linear-gradient(135deg, #0d3b66 0%, #082844 100%)',
          color: 'white',
        }}
      >
        <Typography variant="h5" sx={{ color: 'secondary.main', mb: 1, fontWeight: 600 }}>
          Submit a Deletion Request
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          If you believe we may have any data associated with you and would like it deleted, please submit
          a request below:
        </Typography>

        {!submitted ? (
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ maxWidth: 500 }}
          >
            <TextField
              fullWidth
              required
              type="email"
              label="Email Address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  color: 'white',
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                  '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
                  '&.Mui-focused fieldset': { borderColor: 'secondary.main' },
                },
                '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
                '& .MuiInputLabel-root.Mui-focused': { color: 'secondary.main' },
              }}
            />

            <FormControl fullWidth required sx={{ mb: 2 }}>
              <InputLabel sx={{ color: 'rgba(255,255,255,0.7)', '&.Mui-focused': { color: 'secondary.main' } }}>
                Platform
              </InputLabel>
              <Select
                value={platform}
                onChange={(e) => setPlatform(e.target.value)}
                label="Platform"
                sx={{
                  color: 'white',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.5)' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'secondary.main' },
                  '& .MuiSvgIcon-root': { color: 'white' },
                }}
              >
                <MenuItem value="website">Website</MenuItem>
                <MenuItem value="android-app">Android App (Smart Industries)</MenuItem>
                <MenuItem value="both">Both</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Additional Details (optional)"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  color: 'white',
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                  '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
                  '&.Mui-focused fieldset': { borderColor: 'secondary.main' },
                },
                '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
                '& .MuiInputLabel-root.Mui-focused': { color: 'secondary.main' },
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{
                backgroundColor: 'secondary.main',
                color: '#0d1b2a',
                fontWeight: 700,
                py: 1.5,
                '&:hover': { backgroundColor: 'secondary.dark' },
              }}
            >
              Submit Deletion Request
            </Button>
          </Box>
        ) : (
          <Alert
            icon={<CheckCircleIcon />}
            severity="success"
            sx={{
              maxWidth: 500,
              backgroundColor: 'rgba(46, 204, 113, 0.2)',
              border: '1px solid #2ecc71',
              color: 'white',
              '& .MuiAlert-icon': { color: '#2ecc71' },
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>Request Submitted</Typography>
            <Typography variant="body2">
              Your data deletion request has been submitted. We will process it within 30 days and send
              confirmation to your email address.
            </Typography>
          </Alert>
        )}
      </Paper>

      {/* Contact Section */}
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h5" sx={{ color: 'primary.main', mb: 1, fontWeight: 600 }}>
          Questions?
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          For questions about data deletion or our privacy practices, contact us at:
        </Typography>
        <Typography variant="body1" sx={{ mt: 1.5 }}>
          <Link
            href="mailto:mikel.smart@smartindustriesllc.com?subject=Data%20Deletion%20Inquiry"
            sx={{ color: 'secondary.dark', fontWeight: 600, fontSize: '1.1rem' }}
          >
            mikel.smart@smartindustriesllc.com
          </Link>
        </Typography>
        <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
          See our <Link component={RouterLink} to="/privacy-policy" sx={{ color: 'secondary.dark' }}>Privacy Policy</Link>{' '}
          for more information about how we handle your data.
        </Typography>
      </Paper>
    </Container>
  );
};

export default DataDeletion;
